import { isDefined } from "@clipboard-health/util-ts";
import { addDays, isAfter, startOfDay } from "date-fns";

// A posted visit deadline is a soft "book by" date: the visit stays bookable for this many hours
// past the deadline (see home-health-api's VISIT_DEADLINE_GRACE_PERIOD_HOURS) rather than closing
// the moment the deadline passes. Keep this in sync with the backend grace window (GRO-2573).
export const VISIT_DEADLINE_GRACE_PERIOD_HOURS = 48;

const GRACE_WINDOW_DAYS = VISIT_DEADLINE_GRACE_PERIOD_HOURS / 24;

/**
 * Helper text for the visit due-date picker, shown when the chosen deadline falls within the grace
 * window so admins know a near-term visit will not disappear the instant the deadline passes.
 * Returns undefined when there is nothing to show (empty/invalid date, or a deadline beyond the
 * grace window). Accepts either a `Date` (desktop pickers) or an ISO string (mobile inputs).
 */
export function getDeadlineGraceHint(value: Date | string | null | undefined): string | undefined {
  if (!isDefined(value)) {
    return undefined;
  }

  const deadline = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(deadline.getTime())) {
    return undefined;
  }

  const withinGraceWindow = !isAfter(
    startOfDay(deadline),
    addDays(startOfDay(new Date()), GRACE_WINDOW_DAYS)
  );

  return withinGraceWindow
    ? `This visit will remain open for ${VISIT_DEADLINE_GRACE_PERIOD_HOURS} hours after the deadline unless cancelled.`
    : undefined;
}
